import React, { useMemo, useEffect } from 'react';
import { useParams } from 'react-router';
import { useHistory } from 'react-router-dom';
import { useToast } from '@chakra-ui/react';
import { RegenerateRedirect } from './components/RegenerateRedirect';
import { useGetSitecoreContextId } from '../../api/useGetSitecoreContextId';
import { contextIdsRoute, getContextIdsDetailsRoute } from '../../routes';
import { useGetTenantsQuery } from 'gql/graphql';
import { env as envConfig } from 'features/common/config/envConfig';
import {
  TenantApplicationNode,
  RegenerationErrorType,
} from '../../types/types';
import { logError, getErrorMessage } from '../../utils/errorUtils';
import { RegenerationLoading } from './components/RegenerationLoading';
import { useAnnotationIndex } from '../../hooks/useAnnotationIndex';
import { prefetchDeploymentInfo } from '../../hooks/useDeploymentInfoCache';

export const ContextIdRegeneratePage: React.FC = () => {
  const { sitecoreContextId } = useParams<{ sitecoreContextId: string }>();
  const history = useHistory();
  const toast = useToast();

  const {
    data,
    isLoading: isContextLoading,
    error: contextError,
  } = useGetSitecoreContextId({ sitecoreContextId });

  const {
    data: tenantsData,
    loading: isTenantsLoading,
    error: tenantsError,
  } = useGetTenantsQuery({
    variables: {
      first: 100,
      env: envConfig,
    },
  });

  const isLoading = isContextLoading || isTenantsLoading;
  const error = contextError || tenantsError;

  const rawNodes = useMemo(
    () => tenantsData?.user?.applications?.nodes || [],
    [tenantsData],
  );

  const applicationsNodes: TenantApplicationNode[] = useMemo(
    () =>
      rawNodes.map((node) => ({
        id: node.id,
        annotations:
          node.annotations?.map((a) => ({
            key: a.key,
            value: a.value,
          })) || [],
      })),
    [rawNodes],
  );

  const annotationIndex = useAnnotationIndex(applicationsNodes);

  const hasRequiredAnnotations = useMemo(
    () => annotationIndex.getDeploymentNodes().length > 0,
    [annotationIndex],
  );

  useEffect(() => {
    if (!isLoading && data?.resources && applicationsNodes.length > 0) {
      prefetchDeploymentInfo(
        [sitecoreContextId],
        data.resources,
        applicationsNodes,
      );
    }
  }, [sitecoreContextId, data, isLoading, applicationsNodes]);

  const searchParams = new URLSearchParams(window.location.search);
  let organizationId = searchParams.get('organization');

  const handleRedirectError = (errorMessage?: string) => {
    const message =
      errorMessage ||
      'Could not process the regeneration request. Please ensure that you are trying to regenerate a Master ContextID for a valid tenant.';

    logError('ContextIdRegeneratePage', new Error(message), {
      sitecoreContextId,
      hasResources: Boolean(data?.resources?.length),
      hasTenantsData: Boolean(tenantsData?.user?.applications?.nodes?.length),
    });

    toast({
      status: 'error',
      title: 'Regeneration Error',
      description: message,
      isClosable: false,
      duration: 5000,
    });

    const returnPath = sitecoreContextId
      ? getContextIdsDetailsRoute(sitecoreContextId)
      : contextIdsRoute;

    history.push({
      pathname: returnPath,
      search: history.location.search,
    });
  };

  if (isLoading) {
    return (
      <RegenerationLoading
        isLoading={true}
        loadingMessage='Retrieving tenant information...'
      />
    );
  }

  if (error) {
    logError('ContextIdRegeneratePage', error, { sitecoreContextId });

    toast({
      status: 'error',
      title: 'Data Retrieval Error',
      description:
        'Unable to retrieve required data for regeneration. Redirecting back to context details.',
      isClosable: false,
      duration: 5000,
    });

    setTimeout(() => {
      history.push({
        pathname: sitecoreContextId
          ? getContextIdsDetailsRoute(sitecoreContextId)
          : contextIdsRoute,
        search: history.location.search,
      });
    }, 1000);

    return null;
  }

  if (!organizationId && data?.organizationId) {
    organizationId = data.organizationId;
    const newUrl = new URL(window.location.href);
    newUrl.searchParams.set('organization', organizationId);
    window.history.replaceState({}, '', newUrl.toString());
  }

  if (rawNodes.length === 0) {
    handleRedirectError(
      getErrorMessage(RegenerationErrorType.NO_TENANT_APPLICATIONS),
    );
    return null;
  }

  if (!hasRequiredAnnotations) {
    handleRedirectError(getErrorMessage(RegenerationErrorType.NO_ANNOTATIONS));
    return null;
  }

  return (
    <RegenerateRedirect
      sitecoreContextId={sitecoreContextId}
      resources={data?.resources}
      applicationsNodes={applicationsNodes}
      onRedirectError={handleRedirectError}
    />
  );
};
